import React, { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

import { getCroppedImg } from '@/utils/getCroppedImg.ts';
import Modal from '@/animations/Modal.tsx';
import LottieAnimation from '@/animations/LottieAnimation.tsx';
import loadingAnimationData from '@/components/common/svg/assets/loader_rounded.json';
import {
  btn,
  btnCancel,
  btnConfirm,
  btnContent,
  cropperButtons,
  cropperContainer,
  cropperWrapper
} from '@/components/ProfileData/ProfilePhotos/components/PhotoCropper/style.ts';

interface Props {
  imageSrc: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

interface CroppedArea {
  width: number;
  height: number;
  x: number;
  y: number;
}

export const PhotoCropper: React.FC<Props> = ({
  imageSrc,
  open,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<CroppedArea | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const onCropComplete = useCallback((_: any, croppedPixels: CroppedArea) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onConfirm(croppedBlob);
    } catch (e) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      throw e;
    }
  };

  return (
    <Modal fullScreen open={open} handleClose={onCancel}>
      <div style={cropperContainer}>
        <div style={cropperWrapper}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            maxZoom={1.3}
            aspect={2 / 3}
            showGrid={true}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div style={cropperButtons}>
          <button
            style={{ ...btn, ...btnCancel }}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('button.close')}
          </button>
          <button
            style={{ ...btn, ...btnConfirm }}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            <span style={btnContent}>
              {isSubmitting ? (
                <LottieAnimation
                  width={24}
                  height={24}
                  animationData={loadingAnimationData}
                  shouldAnimate
                  loop
                  color="#fff"
                />
              ) : (
                t('button.confirm')
              )}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
